/**
 * Dashboard del Chofer - Backoffice Web
 * Path: backoffice-react/src/pages/chofer/DashboardChofer.jsx
 * Última modificación: 2026-01-03 17:45
 * Dev: cpalacios01@gmail.com | +595994648273
 * 
 * Dashboard específico para usuarios con role_id = 2 (Choferes)
 * Muestra el redondo asignado y permite registrar boletas y gastos
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  LocalGasStation as GasIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const DashboardChofer = () => {
  const navigate = useNavigate();
  const [redondo, setRedondo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    cargarRedondoActivo();
    
    // Auto-refresh cada 30 segundos
    const interval = setInterval(cargarRedondoActivo, 30000);
    return () => clearInterval(interval);
  }, []);

  const cargarRedondoActivo = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/redondos/activo');
      setRedondo(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setRedondo(null);
      } else {
        setError('Error al cargar el redondo');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-PY').format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-PY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleImprimirResumen = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!redondo) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            No tienes un redondo asignado
          </Typography>
          <Typography variant="body2">
            Contacta al rendidor o administrador para que te asigne un redondo.
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={cargarRedondoActivo}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            Actualizar
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header del Redondo */}
      <Card sx={{ mb: 3, bgcolor: '#2196F3', color: 'white' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {redondo.codigo_unico}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Salida: {formatDateTime(redondo.fecha_salida)}
              </Typography>
            </Box>
            <Chip
              label="EN VIAJE"
              color="success"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Móvil
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.bus?.numero}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Itinerario
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.itinerario?.nombre}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Resumen Financiero */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              Resumen del Viaje
            </Typography>
            <IconButton onClick={handleImprimirResumen} color="primary">
              <PrintIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="space-between" py={1}>
                <Typography color="text.secondary">Monto Inicial:</Typography>
                <Typography fontWeight="600">
                  Gs. {formatMoney(redondo.monto_inicial)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="space-between" py={1}>
                <Typography color="text.secondary">Recaudado:</Typography>
                <Typography fontWeight="600" color="success.main">
                  + Gs. {formatMoney(redondo.total_recaudado)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="space-between" py={1}>
                <Typography color="text.secondary">Gastos:</Typography>
                <Typography fontWeight="600" color="error.main">
                  - Gs. {formatMoney(redondo.total_gastos)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" py={1}>
                <Typography variant="h6" fontWeight="bold">
                  A Entregar:
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Gs. {formatMoney(redondo.debe_entregar)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ textAlign: 'center', py: 3 }}>
            <ReceiptIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h3" fontWeight="bold" color="text.primary">
              {redondo.total_boletas || 0}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Boletas Emitidas
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ textAlign: 'center', py: 3 }}>
            <GasIcon sx={{ fontSize: 60, color: '#FF5722', mb: 2 }} />
            <Typography variant="h3" fontWeight="bold" color="text.primary">
              {redondo.gastos?.length || 0}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gastos Registrados
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Acciones Principales */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate('/chofer/nueva-boleta')}
            sx={{
              py: 3,
              bgcolor: '#4CAF50',
              '&:hover': { bgcolor: '#45a049' },
              fontSize: '1.2rem',
            }}
          >
            NUEVA BOLETA
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<MoneyIcon />}
            onClick={() => navigate('/chofer/nuevo-gasto')}
            sx={{
              py: 3,
              bgcolor: '#FF5722',
              '&:hover': { bgcolor: '#e64a19' },
              fontSize: '1.2rem',
            }}
          >
            NUEVO GASTO
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => navigate('/chofer/detalle-redondo')}
            sx={{ py: 2 }}
          >
            Ver Detalle Completo
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardChofer;

// cpalacios01@gmail.com | +595994648273
